import React from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, ArrowLeft, Copy, Check, Clock, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface DnsRecord {
  type: string;
  name: string;
  value: string;
  purpose: string;
}

interface Verification {
  domain: string;
  expires_at: string;
  days_remaining: number | null;
  dns_records: DnsRecord[];
}

interface DomainStep2ConfigureDNSProps {
  verification: Verification;
  onNext: () => void;
  onBack: () => void;
}

export function DomainStep2ConfigureDNS({ verification, onNext, onBack }: DomainStep2ConfigureDNSProps) {
  const { toast } = useToast();
  const [copiedIndex, setCopiedIndex] = React.useState<number | null>(null);

  const handleCopy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      toast({
        title: 'Copied!',
        description: 'Value copied to clipboard',
      });
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch {
      toast({
        title: 'Failed to copy',
        description: 'Please copy the value manually',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center pb-2">
        <h2 className="text-2xl font-semibold mb-2">Configure DNS Records</h2>
        <p className="text-muted-foreground">
          Add these DNS records to your domain registrar for <strong>{verification.domain}</strong>
        </p>
      </div>

      <Alert className="bg-amber-500/10 border-amber-500/20">
        <Clock className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-700">Time Sensitive</AlertTitle>
        <AlertDescription className="text-amber-600">
          You have {verification.days_remaining} days to complete verification.
          DNS changes may take up to 48 hours to propagate.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        {verification.dns_records.map((record, index) => (
          <Card key={index} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono">
                    {record.type}
                  </Badge>
                  <span className="text-sm text-muted-foreground">{record.purpose}</span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Host / Name</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono break-all">
                      {record.name}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 min-h-[44px] min-w-[44px]"
                      onClick={() => handleCopy(record.name, index * 2)}
                    >
                      {copiedIndex === index * 2 ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Value / Points To</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono break-all">
                      {record.value}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 min-h-[44px] min-w-[44px]"
                      onClick={() => handleCopy(record.value, index * 2 + 1)}
                    >
                      {copiedIndex === index * 2 + 1 ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Alert className="bg-muted/50">
        <ExternalLink className="h-4 w-4" />
        <AlertDescription className="text-sm">
          Need help? Check your domain registrar's documentation for instructions on adding DNS records.
          Common providers: Cloudflare, GoDaddy, Namecheap, Route53.
        </AlertDescription>
      </Alert>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} className="min-h-[44px]">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={onNext} className="min-h-[44px]">
          I've Added the Records
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
